import React, { useState } from "react";
import { MoreVertical, Check, ExternalLink } from "lucide-react";
import { urgencyOf } from "../atoms/UrgencyBadge";
import { INK, INK_2, INK_3, LINE, BG, BURGUNDY, INFO_BG, INFO_INK, BAD_INK, WARN_BG, WARN_INK, OK_BG, OK_INK, BAD_BG } from "../bureauTokens";

const URGENCY_STYLE = {
  bad: { background: BAD_BG, color: BAD_INK },
  warn: { background: WARN_BG, color: WARN_INK },
  ok: { background: OK_BG, color: OK_INK },
  neutral: { background: BG, color: INK_3 },
};

// pill.bad / pill.warn / pill.neutral (styles.css + mon-poste.css) —
// "classique" retombe sur la variante "neutral", pas la couleur neutre de
// base du pill générique.
// "élevé" : vocabulaire réel de JuridiquePonctuelle.js (classique/élevé),
// distinct de haute/moyenne/classique utilisé par les autres profils.
const PRIORITY_STYLE = {
  haute: { background: BAD_BG, color: BAD_INK },
  élevé: { background: BAD_BG, color: BAD_INK },
  moyenne: { background: WARN_BG, color: WARN_INK },
  classique: { background: BG, color: INK_3 },
};

function formatDue(due) {
  if (!due) return "—";
  return `${String(due.getDate()).padStart(2, "0")}/${String(due.getMonth() + 1).padStart(2, "0")}`;
}

// item.kind : "task" (todo réel, clôturable ici) ou "etape" (étape
// Traitements comptables affectée à l'utilisateur, ouverture uniquement —
// la clôture d'une étape a ses propres champs par niveau, gérés dans
// TraitementsComptables/organisms/ClotureStageModal.js).
function FeedItemRow({ item, onOpen, onComplete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const urgency = urgencyOf(item.due);

  return (
    <div
      onClick={() => onOpen(item)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="grid items-center cursor-pointer"
      style={{
        gridTemplateColumns: "84px 1fr auto",
        gap: 14,
        padding: "10px 6px",
        borderTop: `1px solid ${LINE}`,
        background: hover ? BG : "transparent",
      }}
    >
      <span
        className="justify-self-start text-[9px] font-bold whitespace-nowrap"
        style={{ padding: "3px 7px", borderRadius: 999, ...URGENCY_STYLE[urgency.tone] }}
      >
        {urgency.label}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-xs font-semibold truncate" style={{ color: BURGUNDY }}>
          {item.isRevision && (
            <span
              className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
              style={{ background: INFO_BG, color: INFO_INK }}
            >
              Révision
            </span>
          )}
          <span className="truncate">{item.client}</span>
        </div>
        <div className="text-[11.5px] truncate" style={{ color: INK }}>
          {item.title}
        </div>
        {item.detail && (
          <div className="text-[10.5px] truncate" style={{ color: INK_3 }}>
            {item.detail}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2.5 justify-self-end">
        <span
          className="text-[11px] font-semibold whitespace-nowrap flex-shrink-0"
          style={{ color: urgency.tone === "bad" ? BAD_INK : INK_2 }}
        >
          {formatDue(item.due)}
        </span>
        {item.priority && (
          <span
            className="text-[10.5px] px-2 py-0.5 rounded-full flex-shrink-0"
            style={PRIORITY_STYLE[item.priority] || PRIORITY_STYLE.classique}
          >
            {item.priority}
          </span>
        )}
        {item.kind === "task" && (
          <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="p-1.5 rounded-lg transition-colors hover:bg-black/5"
              style={{ color: INK_3 }}
            >
              <MoreVertical size={15} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-8 z-20 w-36 bg-white py-1"
                style={{ border: `1px solid ${LINE}`, borderRadius: 10, boxShadow: "0 12px 30px rgba(0,0,0,.15)" }}
              >
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onComplete(item);
                  }}
                  className="w-full flex items-center gap-2 px-2.5 py-2 text-xs hover:bg-black/5"
                  style={{ color: INK }}
                >
                  <Check size={14} style={{ color: INK_3 }} /> Clôturer
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onOpen(item);
                  }}
                  className="w-full flex items-center gap-2 px-2.5 py-2 text-xs hover:bg-black/5"
                  style={{ color: INK }}
                >
                  <ExternalLink size={14} style={{ color: INK_3 }} /> Ouvrir
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FeedItemRow;
